import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core'

import { DynamicDialogConfig, DynamicDialogInjector, DynamicDialogRef } from 'primeng/dynamicdialog'
import { take } from 'rxjs'

import { CustomDialogService, DeviceService } from '@ti/core/common/services'

import { DialogSheetComponent } from '../components/adaptive-dialog/dialog-sheet.component'

export interface AdaptiveDialogOptions {
  /** On portrait phones the component opens in an `app-bottom-dialog` sheet; the config's look applies elsewhere. */
  sheetOnPhone?: boolean
  dismissableSheet?: boolean
}

/** A dynamic dialog on tablets and desktops that can become a bottom sheet on phones. */
@Injectable()
export class AdaptiveDialogService extends CustomDialogService {
  protected readonly deviceService = inject(DeviceService)
  protected readonly applicationRef = inject(ApplicationRef)
  // The injector PrimeNG's dialogs resolve their content from
  protected readonly parentInjector = inject(Injector)
  protected readonly sheets = new Map<DynamicDialogRef, Type<unknown>>()

  public override open<T>(
    componentType: Type<T>,
    config: DynamicDialogConfig,
    options?: AdaptiveDialogOptions,
  ) {
    if (!options?.sheetOnPhone || !this.isPortraitPhone()) {
      return super.open(componentType, config)
    }
    if (!config.duplicate && this.isOpen(componentType)) {
      return null
    }

    const ref = new DynamicDialogRef<T>()
    this.openSheet(ref, componentType, config, options)
    this.closeOnHistoryBack(ref)
    return ref
  }

  // Matches the mobile CSS breakpoint, so a landscape phone keeps the centred dialog
  protected isPortraitPhone() {
    return this.deviceService.deviceWidth$.getValue() <= 480
  }

  protected isOpen(componentType: Type<unknown>) {
    const dialogs = Array.from(this.dialogComponentRefMap.values())
    return (
      Array.from(this.sheets.values()).includes(componentType) ||
      dialogs.some((dialog) => dialog.instance.childComponentType === componentType)
    )
  }

  protected openSheet<T>(
    ref: DynamicDialogRef<T>,
    componentType: Type<T>,
    config: DynamicDialogConfig,
    options: AdaptiveDialogOptions,
  ) {
    let sheet: ComponentRef<DialogSheetComponent> | undefined
    let state: 'open' | 'closing' | 'hidden' = 'open'
    let isDestroyed = false

    const remove = () => {
      this.sheets.delete(ref)
      const removed = sheet
      sheet = undefined
      if (removed) {
        this.applicationRef.detachView(removed.hostView)
        removed.destroy()
      }
    }

    this.sheets.set(ref, componentType)

    ref.onClose.pipe(take(1)).subscribe(() => {
      state = 'closing'
      sheet?.instance.visible.set(false)
    })
    ref.onDestroy.pipe(take(1)).subscribe(() => {
      isDestroyed = true
      // Reopenable at once, as after PrimeNG's destroy(); callers destroy right after close, so the sheet slides out first
      this.sheets.delete(ref)
      if (state !== 'closing' || !sheet) remove()
    })

    // Callers subscribe to onChildComponentLoaded after open() returns and set fields the content reads on init
    Promise.resolve().then(() => {
      if (isDestroyed || state !== 'open') {
        if (!isDestroyed) ref.destroy()
        return
      }

      sheet = createComponent(DialogSheetComponent, {
        environmentInjector: this.applicationRef.injector,
        elementInjector: new DynamicDialogInjector(
          this.parentInjector,
          new WeakMap<object, unknown>([
            [DynamicDialogConfig, config],
            [DynamicDialogRef, ref],
          ]),
        ),
      })
      sheet.instance.focusOnShow.set(config.focusOnShow !== false)
      // As in PrimeNG's dynamic dialog: the backdrop closes only with dismissableMask, Escape only when closable
      sheet.instance.dismissable.set(!!config.dismissableMask || !!options.dismissableSheet)
      sheet.instance.showDragHandle.set(!!options.dismissableSheet)
      sheet.instance.closeOnEscape.set(config.closeOnEscape !== false && !!config.closable)
      sheet.instance.dismissed.subscribe(() => ref.close())
      sheet.instance.hidden.subscribe(() => {
        if (!sheet) return
        state = 'hidden'
        if (isDestroyed) remove()
        else ref.destroy()
      })
      document.body.appendChild(sheet.location.nativeElement)
      // Rendered first, so the content starts inside the page as in a PrimeNG dialog
      sheet.changeDetectorRef.detectChanges()

      const content = sheet.instance.outlet.createComponent(componentType)
      Object.entries(config.inputValues ?? {}).forEach(([key, value]) =>
        content.setInput(key, value),
      )
      ref.onChildComponentLoaded.next(content.instance)

      sheet.changeDetectorRef.detectChanges()
      this.applicationRef.attachView(sheet.hostView)
    })
  }
}
